import React, { useEffect, type ReactNode } from 'react';
import { ToastContainer } from 'react-toastify';
import BrandLogo from '../BrandLogo';
import 'react-toastify/dist/ReactToastify.css';
import '../../assets/css/auth.css';

/* ───────────────────────── Icons ───────────────────────── */

export const BrandMark = ({ light = false }: { light?: boolean }) => (
  <BrandLogo tone={light ? 'light' : 'dark'} />
);

export const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export const CheckIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

/** Rotating circular text badge – same idea as the home-page video badge. */
export const Badge = () => (
  <div className="ffa-badge" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="ffa-badge-ring">
      <defs>
        <path id="ffa-badge-path" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
      </defs>
      <text>
        <textPath href="#ffa-badge-path" textLength="228" lengthAdjust="spacing">
          FASHION FRAME · NEW COLLECTION ·
        </textPath>
      </text>
    </svg>
    <svg className="ffa-badge-star" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 1l2.6 7.4L22 11l-7.4 2.6L12 21l-2.6-7.4L2 11l7.4-2.6L12 1z" />
    </svg>
  </div>
);

export const Spinner = () => <span className="ffa-spinner" aria-hidden="true" />;

/* ───────────────────────── Modal shell ───────────────────────── */

/** [main arch, side arch, round thumbnail] */
export type AuthGallery = [string, string, string];

interface AuthModalProps {
  onClose: () => void;
  /** three photos for the left-panel collage (desktop only) */
  gallery: AuthGallery;
  panelEyebrow: string;
  panelTitle: ReactNode;
  perks: string[];
  /** id of the heading inside `children` – used for aria-labelledby */
  labelledBy: string;
  /** wider card, used by the sign-up form */
  wide?: boolean;
  children: ReactNode;
}

const AuthModal = ({
  onClose,
  gallery,
  panelEyebrow,
  panelTitle,
  perks,
  labelledBy,
  wide = false,
  children,
}: AuthModalProps) => {
  // lock page scroll while the modal is open
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <ToastContainer />
      <div className="ffa-overlay" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        <div className="ffa-backdrop" onClick={onClose} />

        <div className={`ffa-card${wide ? ' ffa-card--wide' : ''}`}>
          {/* ── Visual panel (hidden on small screens) ── */}
          <div className="ffa-visual">
            <div className="ffa-visual-top">
              <BrandMark light />
            </div>

            <div className="ffa-gallery">
              <div className="ffa-shot ffa-shot--main">
                <img src={gallery[0]} alt="" />
              </div>
              <div className="ffa-shot ffa-shot--side">
                <img src={gallery[1]} alt="" />
              </div>
              <div className="ffa-shot ffa-shot--dot">
                <img src={gallery[2]} alt="" />
              </div>
              <Badge />
            </div>

            <div className="ffa-visual-bottom">
              <p className="ffa-visual-eyebrow">{panelEyebrow}</p>
              <h3 className="ffa-visual-title">{panelTitle}</h3>
              <ul className="ffa-perks">
                {perks.map((perk) => (
                  <li key={perk}>{perk}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Form panel ── */}
          <div className="ffa-panel">
            <button type="button" className="ffa-close" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </button>
            <div className="ffa-mobile-brand">
              <BrandMark />
            </div>
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthModal;